import React, { useState } from "react";
import { Event, EventType } from "@visitproof/types";
import { Text } from "../../../../design/text";
import { Badge } from "../../../../design/badge";
import { Button } from "../../../../design/button";
import moment from "moment";
import {
  EyeIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  StopIcon,
  MapPinIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhoneIcon,
  HandRaisedIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/20/solid";
import { PencilIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../../../contexts/AuthContext";
import EditEventDialog from "../../../VisitDetailPage/components/EditEventDialog";

const getEventTypeConfig = (type: EventType) => {
  switch (type) {
    case "observation":
      return {
        label: "Observation",
        color: "blue" as const,
        icon: EyeIcon,
      };
    case "interaction":
      return {
        label: "Interaction",
        color: "green" as const,
        icon: ChatBubbleLeftRightIcon,
      };
    case "incident":
      return {
        label: "Incident",
        color: "red" as const,
        icon: ExclamationTriangleIcon,
      };
    case "supervisor_started":
      return {
        label: "Visit Started",
        color: "green" as const,
        icon: PlayIcon,
      };
    case "supervisor_ended":
      return {
        label: "Visit Ended",
        color: "zinc" as const,
        icon: StopIcon,
      };
    case "supervisor_arrived":
      return {
        label: "Arrived",
        color: "orange" as const,
        icon: MapPinIcon,
      };
    case "supervisor_enroute":
      return {
        label: "En Route",
        color: "yellow" as const,
        icon: TruckIcon,
      };
    case "supervisor_no_show":
      return {
        label: "No Show",
        color: "red" as const,
        icon: XCircleIcon,
      };
    case "intervention":
      return {
        label: "Intervention",
        color: "purple" as const,
        icon: HandRaisedIcon,
      };
    case "phone_call":
      return {
        label: "Phone Call",
        color: "teal" as const,
        icon: PhoneIcon,
      };
    case "party_arrived":
      return {
        label: "Arrival",
        color: "sky" as const,
        icon: MapPinIcon,
      };
    case "handoff_note":
      return {
        label: "Handoff Notes",
        color: "violet" as const,
        icon: ClipboardDocumentListIcon,
      };
    default:
      return {
        label: type,
        color: "zinc" as const,
        icon: CheckCircleIcon,
      };
  }
};

interface EventCardProps {
  event: Event;
  onEventUpdated?: () => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onEventUpdated }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { isAdmin, claims } = useAuth();

  const config = getEventTypeConfig(event.type);
  const Icon = config.icon;
  const createdAt = event.createdAt ? moment(event.createdAt.toDate()) : moment();

  // Check if user is admin or owner
  const canEdit = isAdmin || claims?.role === "owner";

  const isSystemEvent = [
    "supervisor_started",
    "supervisor_ended",
    "supervisor_arrived",
    "supervisor_enroute",
    "supervisor_no_show",
  ].includes(event.type);

  const handleEventUpdated = () => {
    setIsEditOpen(false);
    if (onEventUpdated) {
      onEventUpdated();
    }
  };

  return (
    <>
    <div className="p-4 border rounded-lg border-zinc-200 dark:border-zinc-700">
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-lg ${
            isSystemEvent
              ? "bg-zinc-100 dark:bg-zinc-800"
              : config.color === "blue"
              ? "bg-blue-100 dark:bg-blue-900/30"
              : config.color === "green"
              ? "bg-green-100 dark:bg-green-900/30"
              : config.color === "red"
              ? "bg-red-100 dark:bg-red-900/30"
              : "bg-zinc-100 dark:bg-zinc-800"
          }`}
        >
          <Icon
            className={`w-5 h-5 ${
              isSystemEvent
                ? "text-zinc-500"
                : config.color === "blue"
                ? "text-blue-600 dark:text-blue-400"
                : config.color === "green"
                ? "text-green-600 dark:text-green-400"
                : config.color === "red"
                ? "text-red-600 dark:text-red-400"
                : "text-zinc-500"
            }`}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <Badge color={config.color}>{config.label}</Badge>
              {event.severity && event.severity !== "low" && (
                <Badge
                  color={
                    event.severity === "high"
                      ? "red"
                      : event.severity === "medium"
                      ? "orange"
                      : "zinc"
                  }
                >
                  {event.severity}
                </Badge>
              )}
            </div>
            <Text className="text-sm text-zinc-500">
              {createdAt.format("h:mm A")}
            </Text>
          </div>

          {event.body && (
            <Text className="mt-2 whitespace-pre-wrap">{event.body}</Text>
          )}

          {event.actionTaken && (
            <div className="p-2 mt-2 rounded bg-zinc-50 dark:bg-zinc-800">
              <Text className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                Action Taken:
              </Text>
              <Text className="text-sm">{event.actionTaken}</Text>
            </div>
          )}

          {event.images && event.images.length > 0 && (
            <div className="flex gap-2 mt-3">
              {event.images.map((image, index) => (
                <a
                  key={index}
                  href={image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-16 h-16 overflow-hidden border rounded-lg border-zinc-200 dark:border-zinc-700"
                >
                  <img
                    src={image}
                    alt={`Event attachment ${index + 1}`}
                    className="object-cover w-full h-full"
                  />
                </a>
              ))}
            </div>
          )}

          {canEdit && (
            <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-700">
              <Button
                plain
                onClick={() => setIsEditOpen(true)}
                className="text-sm"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit Event
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Edit Event Dialog */}
    {isEditOpen && (
      <EditEventDialog
        event={event}
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onEventUpdated={handleEventUpdated}
      />
    )}
    </>
  );
};

export default EventCard;
